/**
 * TrainingContext — global flag that puts the whole app into a safe
 * sandbox for new staff onboarding. When `trainingMode` is true:
 *
 *   • The axios interceptor in `lib/api.js` short-circuits every POST,
 *     PATCH and DELETE before it hits the network. The call returns a
 *     synthesized "fake success" response with a `_training: true`
 *     marker so the UI keeps its happy paths (toasts, optimistic rows,
 *     redirects) without polluting the real audit ledger.
 *   • Reads (GET) still pass through so dashboards/lists look realistic.
 *   • A persistent banner is rendered at the top of <AppLayout> so the
 *     user is never confused about which mode they're in.
 *
 * The flag is persisted to localStorage under `haccp-training-mode` so
 * an iPad left in training mode overnight by the manager doesn't quietly
 * flip back to "real" mode on refresh and silently lose a shift's data.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "haccp-training-mode";

const TrainingContext = createContext({
  trainingMode: false,
  setTrainingMode: () => {},
});

export function TrainingProvider({ children }) {
  const [trainingMode, _setTrainingMode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; }
    catch { return false; }
  });

  // Mirror to localStorage on every flip so a refresh keeps the same
  // state, and so api.js (which is not a React component) can read the
  // same value synchronously.
  useEffect(() => {
    try {
      if (trainingMode) localStorage.setItem(STORAGE_KEY, "1");
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* private-mode browsers — fine */ }
  }, [trainingMode]);

  const value = useMemo(() => ({
    trainingMode,
    setTrainingMode: _setTrainingMode,
  }), [trainingMode]);

  return (
    <TrainingContext.Provider value={value}>{children}</TrainingContext.Provider>
  );
}

export function useTraining() {
  return useContext(TrainingContext);
}

/** Synchronous read for non-React modules (e.g. the axios interceptor).
 *  Returns the latest persisted value. */
export function isTrainingModeActive() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; }
  catch { return false; }
}
